import { useNavigate } from 'react-router-dom'
import { ShoppingBag } from 'lucide-react'
import { useBuildStore } from '@/store/build-store'
import { useConfigStore } from '@/store/config-store'
import { calculateTotal, formatCurrency } from '@/lib/pricing'
import { Button } from '@/components/ui/Button'

export function PriceSummary() {
  const navigate = useNavigate()
  const { selectedModelId, selectedOptionIds, currentStep } = useBuildStore()
  const { config } = useConfigStore()

  if (!config) return null

  const model = config.models.find((m) => m.id === selectedModelId)
  const selectedOptions = config.options.filter((o) => selectedOptionIds.includes(o.id))
  const total = calculateTotal(selectedModelId, selectedOptionIds, config.models, config.options)
  const upgradeTotal = selectedOptions.reduce((s, o) => s + o.price, 0)

  const groupedOptions = selectedOptions.reduce<Record<string, typeof selectedOptions>>((acc, opt) => {
    const cat = config.categories.find((c) => c.id === opt.category_id)
    const key = cat?.name ?? 'Upgrades'
    if (!acc[key]) acc[key] = []
    acc[key].push(opt)
    return acc
  }, {})

  return (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-1">
          <ShoppingBag className="h-4 w-4 text-slate-400" />
          <h2 className="font-semibold text-slate-800 text-sm">Build Summary</h2>
        </div>
        {selectedOptions.length > 0 && (
          <p className="text-xs text-slate-500">{selectedOptions.length} upgrade{selectedOptions.length !== 1 ? 's' : ''} selected</p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {!model ? (
          <p className="text-sm text-slate-400 text-center py-8">No model selected yet.</p>
        ) : (
          <>
            {/* Base model */}
            <div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm font-semibold text-slate-800">{model.name}</span>
                <span className="text-sm text-slate-600">{formatCurrency(model.base_price)}</span>
              </div>
              <div className="text-xs text-slate-400">{model.sqft} sq ft base model</div>
            </div>

            {/* Grouped upgrades */}
            {Object.entries(groupedOptions).map(([category, opts]) => (
              <div key={category}>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{category}</div>
                <div className="space-y-1.5">
                  {opts.map((opt) => (
                    <div key={opt.id} className="flex justify-between items-baseline">
                      <span className="text-sm text-slate-700 leading-snug pr-2">{opt.name}</span>
                      <span className="text-sm text-slate-600 shrink-0">
                        {opt.price === 0 ? '—' : `+${formatCurrency(opt.price)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {selectedOptions.length === 0 && (
              <p className="text-sm text-slate-400 italic">No upgrades selected yet.</p>
            )}
          </>
        )}
      </div>

      {/* Total */}
      {model && (
        <div className="border-t border-slate-200 p-5 space-y-3">
          {upgradeTotal > 0 && (
            <div className="flex justify-between text-sm text-slate-500">
              <span>Upgrades</span>
              <span>+{formatCurrency(upgradeTotal)}</span>
            </div>
          )}
          <div className="flex justify-between items-baseline">
            <span className="font-semibold text-slate-800">Estimated Total</span>
            <span className="text-xl font-bold text-brand-600">{formatCurrency(total)}</span>
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Estimate only. Final pricing subject to review.
          </p>
          {currentStep === 'customize' && (
            <Button fullWidth onClick={() => navigate('/build/review')}>
              Review Build
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
